/**
 * Reminder endpoints — all URLs include trailing slash to avoid FastAPI 307
 * redirects.
 */

import type {
  Reminder,
  ReminderClaim,
  ReminderConvertRequest,
  ReminderConvertResponse,
  ReminderCreate,
  ReminderListFilters,
  ReminderListResponse,
  ReminderStatusTransition,
  ReminderUpdate,
} from '@/types/reminder'
import { apiClient } from './client'
import { repeatedKeyParamsSerializer } from './queryParams'

export const reminders = {
  list: async (
    filters?: ReminderListFilters
  ): Promise<ReminderListResponse> => {
    const response = await apiClient.get<ReminderListResponse>(
      '/api/v1/reminders/',
      { params: filters, paramsSerializer: repeatedKeyParamsSerializer }
    )
    return response.data
  },

  get: async (id: string): Promise<Reminder> => {
    const response = await apiClient.get<Reminder>(`/api/v1/reminders/${id}/`)
    return response.data
  },

  getByNumber: async (n: number): Promise<Reminder> => {
    const response = await apiClient.get<Reminder>(
      `/api/v1/reminders/by-number/${n}/`
    )
    return response.data
  },

  create: async (data: ReminderCreate): Promise<Reminder> => {
    const response = await apiClient.post<Reminder>(
      '/api/v1/reminders/',
      data
    )
    return response.data
  },

  update: async (id: string, data: ReminderUpdate): Promise<Reminder> => {
    const response = await apiClient.put<Reminder>(
      `/api/v1/reminders/${id}/`,
      data
    )
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/reminders/${id}/`)
  },

  transitionStatus: async (
    id: string,
    data: ReminderStatusTransition
  ): Promise<Reminder> => {
    const response = await apiClient.put<Reminder>(
      `/api/v1/reminders/${id}/status/`,
      data
    )
    return response.data
  },

  claim: async (id: string, data: ReminderClaim): Promise<Reminder> => {
    const response = await apiClient.post<Reminder>(
      `/api/v1/reminders/${id}/claim/`,
      data
    )
    return response.data
  },

  convertToTodo: async (
    id: string,
    data: ReminderConvertRequest
  ): Promise<ReminderConvertResponse> => {
    const response = await apiClient.post<ReminderConvertResponse>(
      `/api/v1/reminders/${id}/convert-to-todo/`,
      data
    )
    return response.data
  },
}
